import {
  EnrichedNotificationRule,
  EnrichedUser,
} from '@eppendorf/vnls-audit-event-client';
import { Badge, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { useTranslation } from 'react-i18next';

import { DetailsItem } from '$components/details-item/details-item';

import styles from './sent-message-display.module.scss';

export interface SentMessageMetadata {
  incidentId: string;
  communicationChannels: {
    id: string;
    channels: {
      sms?: boolean;
      email?: boolean;
      push?: boolean;
    };
  }[];
  originEntity: EnrichedNotificationRule;
  affectedEntities?: EnrichedUser[];
}

export interface SentMessageDisplayProps {
  metadata: SentMessageMetadata;
}

export function SentMessageDisplay({ metadata }: SentMessageDisplayProps) {
  const { t } = useTranslation();

  const mergedUsersWithChannels = metadata.affectedEntities?.map((user) => {
    const channels = metadata.communicationChannels.find(
      (channel) => channel.id === user.email,
    )?.channels;

    return {
      ...user,
      channels: {
        sms: channels?.sms ?? false,
        email: channels?.email ?? false,
        push: channels?.push ?? false,
      },
    };
  });

  return (
    <div className="w-full overflow-hidden">
      <DetailsItem label={t('shared.incident')}>
        <div>
          <a
            className={styles.incidentLink}
            target="_blank"
            href={`/notifications/incidents?sidecarRoute=incident/flow&id=${metadata.incidentId}`}
            rel="noreferrer"
          >
            <div className="flex flex__dir--row gap-s flex__ai--center">
              {t('shared.incidentDetails')}{' '}
              <Icon
                name="external-link"
                size={IconSizeClasses.XSmall}
                className="bg-blue-500"
              />
            </div>
          </a>
        </div>
      </DetailsItem>

      <DetailsItem label={t('shared.notificationRule')}>
        <div>
          {metadata.originEntity.name ||
            t('shared.unknown') + t('shared.notificationRule')}
        </div>
      </DetailsItem>

      <DetailsItem label={t('shared.recipients')}>
        <div>
          {mergedUsersWithChannels?.length
            ? mergedUsersWithChannels.map((user) => (
                <div key={user.email} className="flex flex__dir--row gap-s">
                  <span>
                    {`${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                      'Unknown User'}
                  </span>
                  <span className="p-bottom-m">
                    <Badge color="blue" size="small">
                      {[
                        user.channels.sms ? 'SMS' : '',
                        user.channels.email ? 'Email' : '',
                        user.channels.push ? 'App' : '',
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Badge>
                  </span>
                </div>
              ))
            : 'No users affected'}
        </div>
      </DetailsItem>
    </div>
  );
}
